import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Image as ImageIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHARACTER_LIMITS, validateLength } from '../../utils/postValidation';
import { getValueByName } from '../../utils/getValueByName';
import ImageCropper from '../ImageCropper/ImageCropper';

const FieldComponent = ({ field, onFieldChange, selectedValues }) => {
  const { t } = useTranslation();
  const [customValue, setCustomValue] = useState('');
  const [charCounts, setCharCounts] = useState({});
  
  // Image cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    let value = e.target.value;
    if (CHARACTER_LIMITS[field.name]) {
      value = validateLength(field.name, value);
    }
    setCharCounts((prev) => ({
      ...prev,
      [field.name]: value.length,
    }));
    onFieldChange(field.name, value);

    const otherValue = getValueByName(field, 'other');
    if (value === otherValue) {
      setCustomValue('');
      setCharCounts((prev) => ({
        ...prev,
        [`${field.name}_custom`]: 0,
      }));
    }
  };

  const handleCustomChange = (e, fieldName) => {
    let value = e.target.value;
    value = validateLength(fieldName, value);

    setCustomValue(value);
    setCharCounts((prev) => ({
      ...prev,
      [fieldName]: value.length,
    }));

    onFieldChange(fieldName, value);
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let updatedValues = selectedValues[field.name] ? [...selectedValues[field.name]] : [];

    if (checked) {
      if (!updatedValues.includes(value)) {
        updatedValues.push(value);
      }
    } else {
      updatedValues = updatedValues.filter((v) => v !== value);
    }

    onFieldChange(field.name, updatedValues);
  };

  const otherValue = getValueByName(field, 'other');
  const checkboxOtherValue = getValueByName(field, 'other');

  // Input base classes
  const inputClasses = `
    w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl
    bg-white text-gray-900 placeholder-gray-400
    focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
    transition-all duration-200
  `;

  const selectClasses = `
    w-full px-4 py-3 pr-10 text-base border-2 border-gray-200 rounded-xl
    bg-white text-gray-900 appearance-none cursor-pointer
    focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
    transition-all duration-200
  `;

  const textareaClasses = `
    w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl
    bg-white text-gray-900 placeholder-gray-400 min-h-[120px] resize-none
    focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
    transition-all duration-200
  `;

  const labelClasses = 'block text-sm font-semibold text-gray-700 mb-2';
  const charCountClasses = 'text-xs text-gray-400 ml-2';

  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.05 }}
    >
      {/* Label Row */}
      <div className="flex items-center justify-between">
        <label className={labelClasses}>{t(field.label)}</label>
        
        {CHARACTER_LIMITS[field.name] && field.name !== 'animalType' && field.name !== 'title' && (
          <span className={charCountClasses}>
            {charCounts[field.name] || 0}/{CHARACTER_LIMITS[field.name]}
          </span>
        )}
      </div>

      {/* Text Input */}
      {field.type === 'text' && (
        <input
          name={field.name}
          type="text"
          placeholder={t(field.placeholder)}
          className={inputClasses}
          onChange={handleChange}
          maxLength={CHARACTER_LIMITS[field.name] || undefined}
        />
      )}

      {/* Date Input */}
      {field.type === 'date' && (
        <input
          name={field.name}
          type="date"
          className={inputClasses}
          onChange={(e) => onFieldChange(field.name, e.target.value)}
        />
      )}

      {/* Textarea */}
      {field.type === 'textarea' && (
        <textarea
          name={field.name}
          placeholder={t(field.placeholder)}
          className={textareaClasses}
          onChange={handleChange}
          maxLength={CHARACTER_LIMITS[field.name] || undefined}
        />
      )}

      {/* Select */}
      {field.type === 'select' && field.options && Array.isArray(field.options) && (
        <div className="relative">
          <select
            name={field.name}
            className={selectClasses}
            value={selectedValues[field.name] || ''}
            onChange={handleChange}
          >
            <option value="">{t('selectOption', { field: t(field.label) })}</option>
            {field.options.map((option, i) => (
              <option key={i} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      )}

      {/* Custom Input for "Other" in Select */}
      {selectedValues[field.name] === otherValue && field.name !== 'approachable' && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <label className={labelClasses}>{t('other')}</label>
            <span className={charCountClasses}>
              {charCounts[`${field.name}_custom`] || 0}/{CHARACTER_LIMITS[field.name]}
            </span>
          </div>
          <input
            type="text"
            placeholder={t('pleaseSpecify')}
            value={customValue}
            onChange={(e) => handleCustomChange(e, `${field.name}_custom`)}
            className={inputClasses}
            maxLength={CHARACTER_LIMITS[field.name] || undefined}
          />
        </div>
      )}

      {/* Checkbox Group */}
      {field.type === 'checkbox' && field.options && Array.isArray(field.options) && (
        <div className="flex flex-wrap gap-2">
          {field.options.map((option, i) => {
            const isChecked = Array.isArray(selectedValues[field.name]) && 
                              selectedValues[field.name].includes(option.value);
            return (
              <label
                key={i}
                className={`
                  inline-flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer
                  border-2 transition-all duration-200 text-sm font-medium
                  ${isChecked 
                    ? 'bg-primary/10 border-primary text-primary' 
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                  }
                `}
              >
                <input
                  type="checkbox"
                  name={field.name}
                  value={option.value}
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  className="sr-only"
                />
                {t(option.label)}
              </label>
            );
          })}
        </div>
      )}

      {/* Custom Input for "Other" in Checkboxes */}
      {Array.isArray(selectedValues[field.name]) && selectedValues[field.name]?.includes(checkboxOtherValue) && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <label className={labelClasses}>{t('other')}</label>
            <span className={charCountClasses}>
              {charCounts['colorMarkings_custom'] || 0}/{CHARACTER_LIMITS['colorMarkings_custom']}
            </span>
          </div>
          <input
            type="text"
            placeholder={t('pleaseSpecify')}
            value={customValue}
            onChange={(e) => handleCustomChange(e, 'colorMarkings_custom')}
            className={inputClasses}
            maxLength={CHARACTER_LIMITS['colorMarkings_custom']}
          />
        </div>
      )}

      {/* Radio Group (Approachable) */}
      {field.name === 'approachable' && (
        <div className="flex gap-4">
          <label
            className={`
              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer
              border-2 transition-all duration-200 font-medium
              ${selectedValues[field.name] === true
                ? 'bg-green-100 border-green-500 text-green-700'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
              }
            `}
          >
            <input
              type="radio"
              name="approachable"
              value="yes"
              checked={selectedValues[field.name] === true}
              onChange={() => onFieldChange(field.name, "yes")}
              className="sr-only"
            />
            {t('yes')}
          </label>
          <label
            className={`
              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer
              border-2 transition-all duration-200 font-medium
              ${selectedValues[field.name] === false
                ? 'bg-red-100 border-red-500 text-red-700'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
              }
            `}
          >
            <input
              type="radio"
              name="approachable"
              value="no"
              checked={selectedValues[field.name] === false}
              onChange={() => onFieldChange(field.name, "no")}
              className="sr-only"
            />
            {t('no')}
          </label>
        </div>
      )}

      {/* File Input with Image Cropper */}
      {field.type === 'file' && (
        <div className="space-y-3">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            name={field.name}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  setImageToCrop(reader.result);
                  setShowCropper(true);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
          
          {/* Custom upload button / preview */}
          {croppedPreview ? (
            <div className="relative rounded-xl overflow-hidden border-2 border-primary bg-gray-50">
              <img 
                src={croppedPreview} 
                alt={t('preview', 'Preview')}
                className="w-full aspect-[4/3] object-cover"
              />
              {/* Remove button */}
              <button
                type="button"
                onClick={() => {
                  setCroppedPreview(null);
                  onFieldChange(field.name, null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white
                           hover:bg-black/80 transition-colors"
                aria-label={t('remove_image', 'Remove image')}
              >
                <X className="w-4 h-4" />
              </button>
              {/* Change photo button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg
                           bg-white/90 text-gray-700 text-sm font-medium shadow-md
                           hover:bg-white transition-colors"
              >
                {t('change_photo', 'Change Photo')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 
                         px-4 py-8 border-2 border-dashed border-gray-300 rounded-xl
                         bg-gray-50 text-gray-500 cursor-pointer
                         hover:border-primary hover:bg-primary/5 transition-all duration-200"
            >
              <ImageIcon className="w-8 h-8 text-gray-400" />
              <span className="text-sm font-medium">{t('tap_to_upload', 'Tap to upload photo')}</span>
              <span className="text-xs text-gray-400">{t('photo_hint', 'Position your pet in the frame')}</span>
            </button>
          )}
          
          {/* Image Cropper Modal */}
          {showCropper && imageToCrop && (
            <ImageCropper
              imageSrc={imageToCrop}
              aspectRatio={4 / 3}
              onCropComplete={(croppedFile) => {
                const previewUrl = URL.createObjectURL(croppedFile);
                setCroppedPreview(previewUrl);
                onFieldChange(field.name, croppedFile);
                setShowCropper(false);
                setImageToCrop(null);
              }}
              onCancel={() => {
                setShowCropper(false);
                setImageToCrop(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            />
          )}
        </div>
      )}
    </motion.div>
  );
};

export default FieldComponent;
